import { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { loadProtocolFor, silentHoldFor } from './gameLoadProtocols';

export interface GameLoadState {
  phase: 'visible' | 'hiding' | 'gone';
  /** 0–100 when the game reports its progress (or we estimate it), otherwise null. */
  progress: number | null;
}

// Not a guess at load time — only a way out if the game goes silent (hangs, or
// never fires `load`). Restarted by every sign of life from the game. Generous
// because some games say nothing while downloading their engine (Slotserv's 3xx
// studio: up to 12s on a fast connection between `load` and its first message).
const STALL_MS = 45000;
// Silent games are covered for an estimate; never longer than this from launch.
const MAX_SILENT_HOLD_MS = 30000;
// A slow connection stretches the estimate, up to this many times.
const MAX_NETWORK_SCALE = 3;
const FADE_MS = 400; // keep in sync with .splash transition

// Providers often nest the real game a frame or two deep inside the launch page,
// and those inner frames post straight to `top`. Walk up from the sender
// (`parent` is readable cross-origin) to see whether it lives in our iframe.
const isInsideFrame = (source: MessageEventSource | null, frame: Window | null | undefined): boolean => {
  if (!frame) return false;
  let w = source as Window | null;
  for (let depth = 0; w && depth < 10; depth++) {
    if (w === frame) return true;
    if (w === w.parent) return false;
    w = w.parent;
  }
  return false;
};

// Some studios (Habanero, Greentube) post their events as JSON strings.
const parseMessage = (data: unknown): unknown => {
  if (typeof data !== 'string' || data[0] !== '{') return data;
  try {
    return JSON.parse(data);
  } catch (_) {
    return data;
  }
};

// Decides when GameLoadingSplash leaves:
//   • games with a LoadProtocol: when the game says it's ready;
//   • silent games: we can't see their loader, so we estimate. Each provider's
//     loader runs a measured extra time after its page loads (silentHoldFor);
//     how much slower than our reference this player's first page load was
//     tells us how much slower their network is, and stretches that extra.
//     Every further `load` (redirect chains) restarts it.
export function useGameLoadState(
  iframeRef: RefObject<HTMLIFrameElement>,
  launchUrl: string | undefined,
  gameId: string,
) {
  const [state, setState] = useState<GameLoadState>({ phase: 'visible', progress: null });
  const protocol = useMemo(() => (launchUrl ? loadProtocolFor(launchUrl, gameId) : null), [launchUrl, gameId]);
  const silentHold = useMemo(() => silentHoldFor(launchUrl ?? '', gameId), [launchUrl, gameId]);
  const stallTimerRef = useRef<number>();
  const holdTimerRef = useRef<number>();
  const estimateTimerRef = useRef<number>();
  const fadeTimerRef = useRef<number>();
  const startRef = useRef(0);
  const networkScaleRef = useRef<number | null>(null);
  const heardFromGameRef = useRef(false);

  const clearTimers = () => {
    window.clearTimeout(stallTimerRef.current);
    window.clearTimeout(holdTimerRef.current);
    window.clearInterval(estimateTimerRef.current);
  };

  const hide = () => {
    if (fadeTimerRef.current) return;
    clearTimers();
    setState((s) => ({ ...s, phase: 'hiding' }));
    fadeTimerRef.current = window.setTimeout(() => setState((s) => ({ ...s, phase: 'gone' })), FADE_MS);
  };

  const restartStallTimer = () => {
    if (fadeTimerRef.current) return;
    window.clearTimeout(stallTimerRef.current);
    stallTimerRef.current = window.setTimeout(hide, STALL_MS);
  };

  // The network check: how much slower than our reference recording this
  // player's first page load was (1 = as fast, up to MAX_NETWORK_SCALE).
  const networkScale = () => {
    if (networkScaleRef.current === null) {
      const firstLoadMs = performance.now() - startRef.current;
      networkScaleRef.current = Math.min(Math.max(firstLoadMs / silentHold.refLoadMs, 1), MAX_NETWORK_SCALE);
    }
    return networkScaleRef.current;
  };

  // Silent game: (re)schedule the hide for the provider's extra time (scaled by
  // the network check) from this load, and fill the bar towards it so the
  // wait reads as progress.
  const holdForEstimate = () => {
    if (fadeTimerRef.current) return;
    window.clearTimeout(stallTimerRef.current);
    window.clearTimeout(holdTimerRef.current);
    window.clearInterval(estimateTimerRef.current);
    const start = startRef.current;
    const now = performance.now();
    const holdUntil = Math.min(now + silentHold.extraMs * networkScale(), start + MAX_SILENT_HOLD_MS);
    const remaining = Math.max(0, holdUntil - performance.now());
    holdTimerRef.current = window.setTimeout(hide, remaining);
    const tick = () => {
      const pct = ((performance.now() - start) / (holdUntil - start)) * 100;
      setState((s) => ({ ...s, progress: Math.max(s.progress ?? 0, Math.min(pct, 99)) }));
    };
    tick();
    estimateTimerRef.current = window.setInterval(tick, 200);
  };

  // A new launch URL is a new game: start over.
  useEffect(() => {
    if (!launchUrl) return;
    setState({ phase: 'visible', progress: null });
    fadeTimerRef.current = undefined;
    startRef.current = performance.now();
    networkScaleRef.current = null;
    heardFromGameRef.current = false;
    restartStallTimer();

    const handleMessage = (event: MessageEvent) => {
      if (!isInsideFrame(event.source, iframeRef.current?.contentWindow)) return;
      if (process.env.NODE_ENV === 'development') console.debug('[game message]', event.origin, event.data);
      if (!protocol) return;
      heardFromGameRef.current = true;
      const data = parseMessage(event.data);
      if (protocol.isReady(data) || protocol.isBlocking(data)) {
        hide();
        return;
      }
      restartStallTimer();
      const pct = protocol.progress(data);
      if (pct !== null) {
        setState((s) => ({ ...s, progress: Math.max(s.progress ?? 0, Math.min(pct, 100)) }));
      }
    };
    // Some games (GoldenPhoenix) put their iframe into fullscreen as they load,
    // using the activation from the click that opened them. A fullscreen
    // element is drawn above everything, splash included — so while the splash
    // is up, send it back. Afterwards the game may go fullscreen as it likes.
    const handleFullscreen = () => {
      if (fadeTimerRef.current) return;
      if (document.fullscreenElement && document.fullscreenElement === iframeRef.current) {
        document.exitFullscreen().catch(() => {});
      }
    };
    window.addEventListener('message', handleMessage);
    document.addEventListener('fullscreenchange', handleFullscreen);
    return () => {
      window.removeEventListener('message', handleMessage);
      document.removeEventListener('fullscreenchange', handleFullscreen);
      clearTimers();
      window.clearTimeout(fadeTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [launchUrl, protocol]);

  const onIframeLoad = () => {
    if (!protocol) {
      holdForEstimate();
      return;
    }
    if (heardFromGameRef.current) {
      restartStallTimer();
      return;
    }
    // A game that should talk but stays silent past its measured window is
    // most likely the provider's error page — let the player see it.
    if (fadeTimerRef.current) return;
    window.clearTimeout(stallTimerRef.current);
    stallTimerRef.current = window.setTimeout(hide, protocol.firstMessageMs * networkScale());
  };

  return { state, onIframeLoad };
}
